import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CaCardComponent } from '@global-front-components/ui/card';
import { DatosDomicilioConfig, DatosPersonalesConfig, ModoEdicion } from '../../../ui/model/catalogo-config.model';
import { Router, RouterModule } from '@angular/router';
import { DatosPersonalesComponent } from '../../../comun/components/datos-personales/datos-personales.component';
import { CaCheckboxComponent } from '@global-front-components/ui/checkbox';
import { Domicilio, Persona, Poliza } from '../../../comun/model/poliza.model';
import { CaButtonComponent } from '@global-front-components/ui/button';
import { CaIcon } from '@global-front-components/ui/form-field';
import { DatosDomicilioComponent } from '../../../comun/components/datos-domicilio/datos-domicilio.component';
import { DatosAdicionalesComponent } from '../../../comun/components/datos-adicionales/datos-adicionales.component';
import { CatalogoProtocolo, FraccionamientoCatalogo } from '../../../comun/model/catalogo.model';
import { CatalogoSaludApiService } from '../../../comun/services/api/catalogo-salud-api.service';
import { PolizaSalud, RiesgoSalud } from '../../../comun/model/salud.model';
import { PolizaStoreService } from '../../../comun/services/store/poliza-store.service';
import { CatalogoGeneralStoreService } from '../../../comun/services/store/catalogo-general-store.service';
import { PolizaSaludApiService } from '../../services/api/poliza-salud-api.service';
import { GlobalMessageHandlerService } from '../../../comun/services/util/global-message-handler.service';
import { FlujoPolizaNavigationComponent } from '../../../comun/components/base/flujo-poliza-navigation.component';
import { CatalogoSvgStoreService } from '../../../comun/services/store/catalogo-svg-store.service';
import { ModoRecomendador, ValidacionColectivas } from '../../../comun/model/recomendador.model';
import { PolizaSaludStoreService } from '../../services/store/poliza-salud-store.service';

@Component({
	selector: 'app-datos-simulacion-salud',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		TranslateModule,
		CaCardComponent,
		RouterModule,
		DatosPersonalesComponent,
		DatosDomicilioComponent,
		DatosAdicionalesComponent,
		CaCheckboxComponent,
		FormsModule,
		CaButtonComponent,
		CaIcon
	],
	templateUrl: './datos-simulacion-salud.component.html',
	styleUrls: ['./datos-simulacion-salud.component.scss']
})
export class DatosSimulacionSaludComponent extends FlujoPolizaNavigationComponent<PolizaSaludStoreService> implements OnInit, AfterViewInit {
	@Input() esDental = false;
	catalogo: CatalogoProtocolo;
	simularForm: FormGroup;
	tomadorConfig: DatosPersonalesConfig;
	aseguradoConfigs: DatosPersonalesConfig[];
	domicilioTomadorConfig: DatosDomicilioConfig;
	fraccionamientos: FraccionamientoCatalogo[];
	tomadorEsAsegurado = true;
	mostrarOtrosAsegurados = false;
	tomador: Persona = {};
	asegurados: Persona[];
	domicilioTomador: Domicilio = {};
	propuesta: PolizaSalud = {};

	constructor(
		router: Router,
		polizaStore: PolizaSaludStoreService,
		catalogoStore: CatalogoSvgStoreService,
		private catalogoSalud: CatalogoSaludApiService,
		private catalogoGeneralStore: CatalogoGeneralStoreService,
		private polizaSaludService: PolizaSaludApiService,
		private fb: FormBuilder,
		private translateService: TranslateService,
		private globalMessageHandler: GlobalMessageHandlerService
	) {
		super(router, polizaStore, catalogoStore);
	}

	ngAfterViewInit(): void {
		const fechaNacimientoCtrl = this.simularForm.get('datosPersonalesTomador.tomador_fechaNacimiento');
		fechaNacimientoCtrl?.setValidators([Validators.required]);
		const provinciaCtrl = this.simularForm.get('datosDomicilioTomador.tomador_provincia');
		provinciaCtrl?.setValidators([Validators.required]);
	}

	ngOnInit(): void {
		// Suscripción a la póliza
		this.polizaStore.polizaSubject$.subscribe(p => {
			this.propuesta = p as PolizaSalud || {};
		});

		// 1. Extraer asegurados de los riesgos
		this.asegurados = [];
		if (this.propuesta?.riesgos?.length) {
			this.propuesta.riesgos.forEach(riesgo => {
				if (riesgo.asegurado) {
					this.asegurados.push({ ...riesgo.asegurado });
				}
			});
		}

		// 2. Generar configuraciones con roles indexados
		this.generarConfiguracionesAsegurados();

		// 4. Configurar el tomador
		this.tomador = this.propuesta?.tomador || {};
		this.domicilioTomador = this.propuesta?.tomador?.domicilio || {};

		this.tomadorConfig = {
			persona: this.propuesta?.tomador || {},
			rol: 'tomador',
			modoEdicion: ModoEdicion.SI,
			mostrarDocumento: true,
			mostrarNombre: false,
			mostrarFechaNacimiento: this.tomadorEsAsegurado,
			mostrarSexo: false
		};

		// 5. Si hay asegurados, activar la sección (opcional)
		if (this.asegurados.length > 0) {
			this.mostrarOtrosAsegurados = true;
		}

		this.domicilioTomadorConfig = {
			domicilio: this.domicilioTomador || {},
			rol: 'tomador',
			modoEdicion: ModoEdicion.SI,
			mostrarVia: false,
			mostrarLocalidad: false,
			mostrarProvincia: true
		};

		// 3. Inicializar o REINICIALIZAR el formulario
		this.simularForm = this.fb.group({
			datosPersonalesTomador: this.fb.group({}),
			datosDomicilioTomador: this.fb.group({}),
			datosAdicionales: this.fb.group({}),
			datosAsegurados: this.fb.group({
				asegurados: this.fb.array(this.asegurados.map(a => this.crearFormGroupAsegurado(a)))
			}),
		});


		// Cargar fraccionamientos (independiente)
		this.catalogoSalud.getFraccionamientos(this.translateService.currentLang || 'ES').subscribe(
			fraccionamientos => this.fraccionamientos = fraccionamientos
		);
	}

	private crearFormGroupAsegurado(asegurado: Persona): FormGroup {
		return this.fb.group({ fechaNacimiento: [asegurado.fechaNacimiento ? new Date(asegurado.fechaNacimiento) : null], });
	}

	private generarConfiguracionesAsegurados(): void {
		this.aseguradoConfigs = this.asegurados.map((asegurado, index) => ({
			persona: asegurado,
			rol: `asegurado_${index + 1}`,
			modoEdicion: ModoEdicion.SI,
			mostrarDocumento: false,
			mostrarNombre: false,
			mostrarFechaNacimiento: true,
			mostrarSexo: false
		}));
	}

	// getter para acceder al FormArray
	get aseguradosArray(): FormArray {
		return this.simularForm.get('datosAsegurados.asegurados') as FormArray;
	}

	addAsegurado(): void {
		const nuevoAsegurado: Persona = {};
		this.asegurados.push(nuevoAsegurado);
		this.generarConfiguracionesAsegurados(); // actualiza roles: asegurado_1, asegurado_2...

		// Añadir al FormArray
		this.aseguradosArray.push(this.crearFormGroupAsegurado(nuevoAsegurado));
	}

	/**
	 * No permite quitar el último asegurado si tomador NO es asegurado.
	 * Tampoco permite dejar 0 asegurados en general (mínimo 1).
	 */
	removeAsegurado(index: number): void {
		if (this.asegurados.length <= 1) return;

		this.asegurados.splice(index, 1);
		this.aseguradosArray.removeAt(index);
		this.generarConfiguracionesAsegurados();
	}

	/** Si desmarcas que el tomador es asegurado -> forzar que aparezca al menos 1 asegurado */
	changeTomadorAsegurado(event: { source: any; checked: boolean }) {
		this.tomadorEsAsegurado = event.checked;
		this.tomadorConfig = { ...this.tomadorConfig, mostrarFechaNacimiento: this.tomadorEsAsegurado };
		const fechaNacimientoCtrl = this.simularForm.get('datosPersonalesTomador.tomador_fechaNacimiento');

		if (this.tomadorEsAsegurado) {
			fechaNacimientoCtrl?.setValidators([Validators.required]);
		} else {
			fechaNacimientoCtrl?.clearValidators();
			// forzar a mostrar asegurados y garantizar al menos 1
			this.mostrarOtrosAsegurados = true;
			if (this.aseguradosArray.length === 0) {
				this.addAsegurado();
			}
		}
		fechaNacimientoCtrl?.updateValueAndValidity();
	}

	changeMostrarOtrosAsegurados(event: { source: any; checked: boolean }) {
		const checked = event.checked;
		if (!checked && !this.tomadorEsAsegurado) {
			// no permitir ocultar si tomador no es asegurado
			this.mostrarOtrosAsegurados = true;
			return;
		}

		this.mostrarOtrosAsegurados = checked;

		if (this.mostrarOtrosAsegurados && this.aseguradosArray.length === 0) {
			this.addAsegurado();
		} else if (!this.mostrarOtrosAsegurados) {
			// opcional: limpiar si ocultas (ya lo tenías)
			while (this.aseguradosArray.length) {
				this.aseguradosArray.removeAt(0);
			}
			this.asegurados = [];
		}
	}

	// recibe cambios desde app-datos-personales para cada asegurado
	onAseguradoChange(index: number, persona: Persona): void {
		this.asegurados[index] = persona;
	}

	public onTomadorChange(persona: Persona): void {
		this.tomador = persona;
	}

	public onDomicilioTomadorChange(domicilio: Domicilio): void {
		this.domicilioTomador = domicilio;
	}

	continuar(): void {
		if (this.simularForm.valid
			&& this.tomador?.domicilio?.codProvincia !== '-1'
			&& ((this.tomadorEsAsegurado && this.tomador?.fechaNacimiento) || this.asegurados.length > 0)) {

			if (!this.esDental) {
				const fechasNacimiento = this.buildFechasNacimiento();

				this.polizaSaludService.colectivasValidasTarificacionPorEdad(fechasNacimiento).subscribe({
					next: (resultado: ValidacionColectivas) => {
						if (!resultado || resultado.colectivas.length === 0) {
							this.globalMessageHandler.emitError('Ninguna colectiva disponible para las edades proporcionadas');
							return;
						}
						 // La colectiva senior es siempre la última del array, si existe, la unica en caso senior
						let colectivaSenior = null;
						if (resultado.colectivas.length > 0 &&
							(resultado.modoRecomendador === ModoRecomendador.SENIOR || resultado.modoRecomendador === ModoRecomendador.NORMAL_CON_SENIOR)){
							colectivaSenior = parseInt(resultado.colectivas[resultado.colectivas.length - 1]);
						}	
						this.navegarAlRecomendador(colectivaSenior, resultado.modoRecomendador);
					},
					error: () => {
						this.globalMessageHandler.emitError('Error al validar colectivas por edad');
						return;
					}
				});
			} else {
				this.navegarAlRecomendador(null, ModoRecomendador.NORMAL);
			}
		} else {
			this.marcarTodosControles(this.simularForm);
		}
	}

	/**
	 * Construye el array de fechas de nacimiento que se pasará al endpoint.
	 * Incluye la fecha del tomador solo si es asegurado y válido, y las fechas de todos los asegurados.
	 */
	private buildFechasNacimiento(): string[] {
		const fechas: string[] = [];

		if (this.tomadorEsAsegurado && this.tomador?.fechaNacimiento) {
			fechas.push(new DatePipe('es-ES').transform(this.tomador.fechaNacimiento, 'dd/MM/yyyy'));
		}

		for (const a of this.asegurados) {
			if (a?.fechaNacimiento) {
				fechas.push(new DatePipe('es-ES').transform(a.fechaNacimiento, 'dd/MM/yyyy'));
			}
		}
		return fechas;
	}

	private navegarAlRecomendador(colectivaSenior: number | null, modo: ModoRecomendador): void {
		this.setPolizaEnStore();

		const idTipoDocumento = this.simularForm.get('datosPersonalesTomador')?.get('tomador_tipo-documento')?.value;

		this.catalogoGeneralStore.getTiposDocumento().subscribe(tiposDoc => {
			const tipoDoc = tiposDoc.find(td => td.idTipoDocumento === idTipoDocumento);
			const tipoParam = tipoDoc?.descTipoDocumento ?? tipoDoc?.idTipoDocumento;

			this.navigate('salud/tarificar-recomendador', true, {
				tipoDocumento: tipoParam,
				esDental: this.esDental,
				colectivaSenior: colectivaSenior,
				modoRecomendador: modo
			});
		});
	}

	private setPolizaEnStore() {
		this.propuesta.codigoFraccionamiento = this.simularForm.get('datosAdicionales')?.get('codFrac')?.value;
		this.propuesta.fechaEfecto = this.simularForm.get('datosAdicionales')?.get('fEfecto')?.value;
		this.tomador.domicilio = this.domicilioTomador;
		this.propuesta.tomador = this.tomador;
		this.propuesta.codigoIdioma = this.translateService.currentLang?.toUpperCase() || 'ES';
		if (this.asegurados.length > 0) {
			const riesgos: RiesgoSalud[] = [];
			for (const a of this.asegurados) {
				riesgos.push({ asegurado: a });
			}
			this.propuesta.riesgos = riesgos;
		}
		this.polizaStore.setPoliza(this.propuesta);
	}

	// método auxiliar recursivo
	private marcarTodosControles(formGroup: FormGroup | FormArray): void {
		Object.keys(formGroup.controls).forEach(key => {
			const control = formGroup.get(key);
			if (control instanceof FormGroup || control instanceof FormArray) {
				this.marcarTodosControles(control);
			} else {
				control?.markAsTouched();
				control?.updateValueAndValidity();
			}
		});
	}

}
